import React, { useEffect, useState } from "react";
import SEO from "../common/SEO";
import Header from "./common/Header";
import SideBar from "./common/Sidebar";
import Chart from "react-apexcharts";
import {
  Clock,
  Target,
  Award,
  Calendar,
  BookOpen,
  TargetIcon,
} from "lucide-react";
import axios from "axios";
import API_URL from "../common/config";
import "./Translate/i18n";
import { useTranslation } from "react-i18next";

const ParentDashboard = () => {
  const [studentData, setStudentData] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progressData, setProgressData] = useState([]);
  const [assessmentData, setAssessmentData] = useState([]);
  const [watchTime, setWatchTime] = useState("00:00:00");
  const [clifScore, setClifScore] = useState(0);
  const [latestCourse, setLatestCourse] = useState("");

  const parentData = JSON.parse(localStorage.getItem("parentData"));
  const parentId = localStorage.getItem("parentId");

  useEffect(() => {
    userDetails();
  }, []);

  useEffect(() => {
    if (selectedStudent?._id) {
      userProgressData();
    }
  }, [selectedStudent?._id]);

  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("language", lng);
  };

  const userDetails = async () => {
    try {
      const response = await axios.post(`${API_URL}/parent/getUserDetails`, {
        ids: parentData.student_ids,
      });
      if (response.data.success) {
        setStudentData(response.data.studentData);
        setSelectedStudent(response.data.studentData[0]); // default selection
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const userProgressData = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/parent/getStudentData`, {
        parentId: parentId,
        studentId: selectedStudent._id,
      });
      if (response.data) {
        console.log(response.data);

        // Set the data from API response
        setProgressData(response.data.studentProgressData || []);
        setAssessmentData(response.data.assessmentData || []);
        setWatchTime(response.data.totalWatchTime || "00:00:00");
        setClifScore(response.data.clifScore?.currentClifScore || 0);

        // Get latest course name from progress data
        if (
          response.data.studentProgressData &&
          response.data.studentProgressData.length > 0
        ) {
          const latestProgress = response.data.studentProgressData.sort(
            (a, b) =>
              new Date(b.lastWatched || b.createdAt) -
              new Date(a.lastWatched || a.createdAt)
          )[0];
          setLatestCourse(latestProgress.course?.title || "No Course");
        }

        setLoading(false);
      }
    } catch (error) {
      console.log("#error", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  if (!parentData || !parentId) {
    window.location.href = "/parent-login";
    return null;
  }

  const selectStudent = (studentId) => {
    const student = studentData.find((s) => s._id === studentId);
    setSelectedStudent(student);
  };

  // Generate dynamic progress chart data based on actual progress data
  const generateProgressChartData = () => {
    if (!progressData || progressData.length === 0) {
      return {
        series: [{ name: "Progress", data: [0, 0, 0, 0, 0, 0] }],
        categories: [
          "Week 1",
          "Week 2",
          "Week 3",
          "Week 4",
          "Week 5",
          "Week 6",
        ],
      };
    }

    // Group progress data by week/time periods
    const weeklyProgress = {};
    const currentDate = new Date();

    progressData.forEach((item) => {
      const itemDate = new Date(item.lastWatched || item.createdAt);
      const weeksDiff = Math.floor(
        (currentDate - itemDate) / (7 * 24 * 60 * 60 * 1000)
      );
      const weekKey = Math.max(0, Math.min(5, 0 - weeksDiff)); // Last 6 weeks

      if (!weeklyProgress[weekKey]) {
        weeklyProgress[weekKey] = [];
      }
      weeklyProgress[weekKey].push(item.progress || 0);
    });

    // Calculate average progress for each week
    const progressValues = [];
    const categories = [];

    for (let i = 0; i < 6; i++) {
      const weekData = weeklyProgress[i] || [];
      const avgProgress =
        weekData.length > 0
          ? Math.round(
              weekData.reduce((sum, val) => sum + val, 0) / weekData.length
            )
          : 0;
      progressValues.push(avgProgress);
      categories.push(`Week ${i + 1}`);
    }

    return {
      series: [{ name: "Progress (%)", data: progressValues }],
      categories: categories,
    };
  };

  // Generate lesson content data based on actual modules/videos
  const generateLessonContentData = () => {
    if (!progressData || progressData.length === 0) {
      return {
        series: [33, 33, 34],
        labels: ["No Data", "Available", "Yet"],
      };
    }

    // Group by module or video types
    const contentTypes = {};
    progressData.forEach((item) => {
      const moduleTitle =
        item.module?.description || item.video?.title || "Other";
      const key =
        moduleTitle.length > 20
          ? moduleTitle.substring(0, 20) + "..."
          : moduleTitle;

      if (!contentTypes[key]) {
        contentTypes[key] = 0;
      }
      contentTypes[key] += item.progress || 0;
    });

    const labels = Object.keys(contentTypes).slice(0, 3); // Show top 3
    const series = labels.map((label) => Math.round(contentTypes[label]));

    // If we have less than 3 categories, fill with default
    while (labels.length < 3) {
      labels.push(`Content ${labels.length + 1}`);
      series.push(0);
    }

    return { series, labels };
  };

  const progressChartData = generateProgressChartData();
  const lessonContentData = generateLessonContentData();

  const semesterGradeOptions = {
    chart: { type: "bar", height: 400, toolbar: { show: false } },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "60%",
        dataLabels: { position: "top" },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
      offsetY: -20,
      style: { fontSize: "12px", colors: ["#006600"] },
    },
    xaxis: {
      categories: progressChartData.categories,
    },
    yaxis: { max: 100, title: { text: "Progress %" } },
    colors: ["#6600CC"],
    title: {
      text: "Weekly Learning Progress",
      align: "center",
      style: { fontSize: "16px", fontWeight: "600", color: "#2c3e50" },
    },
  };

  const lessonContentOptions = {
    chart: { type: "donut" },
    labels: lessonContentData.labels,
    colors: ["#6600cc", "#6600ff", "#6600bb"],
    legend: {
      position: "right",
      markers: { width: 12, height: 12 },
      itemMargin: { horizontal: 10, vertical: 10 },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Progress",
              formatter: () => {
                const total = lessonContentData.series.reduce(
                  (sum, val) => sum + val,
                  0
                );
                return `${Math.round(total)}%`;
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Math.round(val)}%`,
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
    },
    title: {
      text: "Learning Content Distribution",
      align: "center",
      style: { fontSize: "16px", fontWeight: "600", color: "#2c3e50" },
    },
  };

  return (
    <>
      <SEO />
      {loading && <Loader />}
      <div className="main-wrapper">
        <SideBar />
        <div className="page-wrapper">
          {/* <Header /> */}
          <div className="container-fluid p-4">
            {/* Dashboard Header */}
            <div className="d-flex justify-content-between mb-3 mt-3">
              <div className="dashboard-header mb-4">
                <h2 className="mb-2">{t("dashboardTitle")}</h2>
              </div>
              <div
                className="btn-group"
                role="group"
                aria-label={t("selectLanguage")}
              >
                <button
                  onClick={() => changeLanguage("en")}
                  className="btn border"
                  style={{
                    backgroundColor:
                      i18n.language === "en" ? "#006600" : "#fff",
                    color: i18n.language === "en" ? "#fff" : "#000",
                  }}
                >
                  English
                </button>
                <button
                  onClick={() => changeLanguage("sp")}
                  className="btn border"
                  style={{
                    backgroundColor:
                      i18n.language === "sp" ? "#006600" : "#fff",

                    color: i18n.language === "sp" ? "#fff" : "#000",
                  }}
                >
                  Spanish
                </button>
              </div>
            </div>

            {/* Student Selector Cards */}
            <div className="row g-4">
              {studentData.map((student, idx) => (
                <div className="col-md-2" key={idx}>
                  <div
                    className={`card shadow-sm border-0 rounded-3 text-center cursor-pointer`}
                    onClick={() => selectStudent(student._id)}
                    style={{
                      cursor: "pointer",
                      backgroundColor:
                        selectedStudent?._id === student._id
                          ? "#006600"
                          : "#6600CC",
                    }}
                  >
                    <div className="card-body d-flex justify-content-center p-2">
                      <h5
                        className="card-title text-white text-capitalize text-nowrap"
                        title={`${student.firstname} ${student.lastname}`}
                      >
                        {student.firstname} {student.lastname}
                      </h5>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Student Name */}
            {selectedStudent && (
              <div className="text-center my-3">
                <h4 className="text-dark">
                  {t("viewingDetails")}{" "}
                  <span className="text-primary text-capitalize">
                    {selectedStudent.firstname} {selectedStudent.lastname}
                  </span>
                </h4>
              </div>
            )}

            {/* Metrics Section */}
            <div className="row mb-4 mt-3">
              <div className="col-lg-4 col-md-6 mb-3">
                <div className="card stat-card h-100 border-0 shadow-sm">
                  <div className="card-body text-center p-4">
                    <h5
                      className="stat-label mb-2 d-flex align-items-center justify-content-center gap-2"
                      style={{
                        backgroundColor: "#6600CC",
                        color: "white",
                        padding: "8px",
                        borderRadius: "5px",
                      }}
                    >
                      <TargetIcon size={40} /> {t("courseName")}
                    </h5>
                    <p className="stat-value h2 mb-0" title={latestCourse}>
                      {latestCourse.length > 30
                        ? latestCourse.substring(0, 30) + "..."
                        : latestCourse || "No Course"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-lg-4 col-md-6 mb-3">
                <div className="card stat-card h-100 border-0 shadow-sm">
                  <div className="card-body text-center p-4">
                    <h5
                      className="stat-label mb-2 d-flex align-items-center justify-content-center gap-2"
                      style={{
                        backgroundColor: "#6600CC",
                        color: "white",
                        padding: "8px",
                        borderRadius: "5px",
                      }}
                    >
                      <Clock size={40} className="text-white" />
                      {t("timeSpent")}
                    </h5>
                    <p className="stat-value h3 mb-0">{watchTime}</p>
                  </div>
                </div>
              </div>

              <div className="col-lg-4 col-md-6 mb-3">
                <div className="card stat-card h-100 border-0 shadow-sm">
                  <div className="card-body text-center p-4">
                    <h5
                      className="stat-label mb-2 d-flex align-items-center justify-content-center gap-2"
                      style={{
                        backgroundColor: "#6600CC",
                        color: "white",
                        padding: "8px",
                        borderRadius: "5px",
                      }}
                    >
                      <Award size={40} className="text-white" />
                      {t("performanceScore")}
                    </h5>
                    <p className="stat-value h3 mb-0">{clifScore}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Summary */}
            <div className="row mb-4">
              <div className="col-sm-12 h-50">
                <div className="card shadow-sm border-0 shadow-sm">
                  <div className="card-head">
                    <h5 className="card-title mx-3 mt-3">
                      {t("video progress")}
                    </h5>
                  </div>
                  <div className="card-body g-3">
                    <div className="row text-center">
                      <div className="col-md-3 mx-3 stat-card h-100 border-0">
                        <div className="p-3">
                          <h6 className="text-muted">{t("totalVideos")}</h6>
                          <h4 className="text-primary">
                            {progressData.length}
                          </h4>
                        </div>
                      </div>
                      <div className="col-md-3 mx-3 stat-card h-100 border-0">
                        <div className="p-3">
                          <h6 className="text-muted">
                            {t("Completed Videos")}
                          </h6>
                          <h4 className="text-success">
                            {
                              progressData.filter((item) => item.completed)
                                .length
                            }
                          </h4>
                        </div>
                      </div>
                      <div className="col-md-3 mx-3 stat-card h-100 border-0">
                        <div className="p-3">
                          <h6 className="text-muted">
                            {t("Average Progress")}
                          </h6>
                          <h4 className="text-warning">
                            {progressData.length > 0
                              ? Math.round(
                                  progressData.reduce(
                                    (sum, item) => sum + (item.progress || 0),
                                    0
                                  ) / progressData.length
                                )
                              : 0}
                            %
                          </h4>
                        </div>
                      </div>
                      <div className="col-md-3 mx-3 stat-card h-100 border-0">
                        <div className="p-3">
                          <h6 className="text-muted">{t("Assessments")}</h6>
                          <h4 className="text-info">{assessmentData.length}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hint */}
            <div className="mt-4 text-center">
              <small className="text-muted">{t("hint")}</small>
            </div>

            {/* Charts Section */}
            <div className="row">
              <div className="col-lg-6 mb-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-header bg-transparent border-0 pt-4 pb-0">
                    <div className="d-flex align-items-center">
                      <Calendar size={24} className="me-2" />
                      <h4 className="mb-0">{t("progressGraph")}</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <Chart
                      options={semesterGradeOptions}
                      series={progressChartData.series}
                      type="bar"
                      height={300}
                    />
                    <div className="text-end mt-2">
                      <small className="text-muted">
                        {t("weeklyPerformance")}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-6 mb-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-header bg-transparent border-0 pt-4 pb-0">
                    <div className="d-flex align-items-center">
                      <BookOpen size={24} className="me-2" />
                      <h4 className="mb-0">{t("lessonContent")}</h4>
                    </div>
                  </div>
                  <div className="card-body">
                    <Chart
                      options={lessonContentOptions}
                      series={lessonContentData.series}
                      type="donut"
                      height={300}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .dashboard-header h2 {
          color: #2c3e50;
          font-weight: 600;
        }

        .stat-card {
          transition: transform 0.3s ease-in-out;
          background: #f8f9fa;
        }

        .stat-card:hover {
          transform: translateY(-5px);
        }

        .stat-label {
          font-size: 0.9rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          color: #2c3e50;
          font-weight: 600;
        }

        .card {
          border-radius: 10px;
        }

        .card-header h4 {
          color: #2c3e50;
          font-size: 1.2rem;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .dashboard-header h2 {
            font-size: 1.5rem;
          }

          .stat-label {
            font-size: 0.8rem;
          }

          .stat-value {
            font-size: 1.2rem;
          }
        }
      `}</style>
    </>
  );
};

export default ParentDashboard;

const Loader = () => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
    }}
  >
    <div
      style={{
        backgroundColor: "white",
        padding: "2rem",
        borderRadius: "8px",
        textAlign: "center",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      }}
    >
      <div
        className="spinner-border text-primary"
        role="status"
        style={{ width: "3rem", height: "3rem" }}
      >
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-3 mb-0">Fetching students progress report...</p>
    </div>
  </div>
);
